import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { CodeBlock } from '../shared';

/**
 * The nav data arrives as props rather than an import: this page is listed in
 * registry.js, so importing the registry back would be a cycle.
 */

export const meta = {
  title: 'Design System',
  description:
    'A multi-brand design system for CLS, ABC and Overall. Visual values come from Figma variables and text styles, are generated into CSS custom properties, and are consumed by components — never hardcoded along the way.',
};

const Stat = ({ label, value }) => (
  <div className="ds-page__stat">
    <span className="typo-body-sm ds-page__stat-label">{label}</span>
    <span className="typo-h2 ds-page__stat-value">{value}</span>
  </div>
);

Stat.propTypes = { label: PropTypes.string, value: PropTypes.node };

export default function OverviewPage({ brand = 'CLS', groups = [], pages = [], basePath = '' }) {
  const documented = pages.filter((p) => p.module.meta?.component).length;

  return (
    <article>
      <header className="ds-page__header">
        <h1 className="typo-h2 ds-page__title">{meta.title}</h1>
        <p className="typo-body-lg ds-page__description">{meta.description}</p>
      </header>

      <div className="ds-page__stat-row">
        <Stat label="Pages" value={pages.length} />
        <Stat label="Groups" value={groups.length} />
        <Stat label="Documented" value={documented} />
        <Stat label="Brand" value={brand} />
      </div>

      <section className="ds-page__section">
        <h2 className="typo-h3 ds-page__section-title">Browse</h2>
        <div className="ds-page__overview-grid">
          {groups.map((group) => (
            <Link
              key={group.id}
              to={`${basePath}/${group.items[0].slug}`}
              className="ds-page__overview-card"
            >
              <span className="typo-h5 ds-page__overview-card-title">{group.label}</span>
              <span className="typo-body-sm ds-page__overview-card-count">
                {group.items.length} {group.items.length === 1 ? 'page' : 'pages'}
              </span>
              <span className="typo-body ds-page__overview-card-items">
                {group.items.map((i) => i.label).join(' · ')}
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="ds-page__section">
        <h2 className="typo-h3 ds-page__section-title">How a page is built</h2>
        <p className="typo-body ds-page__section-note">
          Every page in this dashboard renders through the same scaffold — title, description, demos,
          Props, Usage. The props table is hand-written, but a test cross-checks it against the
          component&apos;s real <code>propTypes</code>, so a renamed or added
          prop fails the suite until the docs catch up.
        </p>
        <CodeBlock
          code={`// pages/ButtonPage.jsx
export const meta = {
  component: Button,          // the drift test reads its propTypes
  title: 'Button',
  description: '…',
  props: [
    ['size', "'primary' | 'secondary' | 'tertiary'", "'primary'", 'Size variant'],
  ],
  usage: [{ title: 'Basic', code: '<Button>Save</Button>' }],
};

export default function ButtonPage() {
  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Sizes">…</DemoCard>
    </ComponentPage>
  );
}

// registry.js — one entry is all it takes to add a page
{ slug: 'button', label: 'Button', icon: MousePointerClick, module: ButtonPage }`}
        />
      </section>

      <section className="ds-page__section">
        <h2 className="typo-h3 ds-page__section-title">The pipeline</h2>
        <p className="typo-body ds-page__section-note">
          Figma is the source of truth for visual values. Never hand-edit anything in
          <code> src/styles/generated/</code> — fix the export and regenerate.
        </p>
        <CodeBlock
          code={`Figma Variables + Text & Effect Styles
  ↓ export
src/figma-export/*.tokens.json · MCP Version-variables.json (styles) · fonts.config.json
  ↓ npm run build:tokens
src/styles/generated/theme.css · typography.css · effects.css · index.css
  ↓ npm run sync:components
src/styles/generated/component-tokens.css
  ↓
src/components/_designSystem/**     ← consume tokens only

# Everything at once
npm run figma:sync`}
        />
      </section>

      <section className="ds-page__section">
        <h2 className="typo-h3 ds-page__section-title">Rules that hold everywhere</h2>
        <ul className="typo-body" style={{ color: 'var(--ds-doc-ink)', maxWidth: '70ch', paddingLeft: '1.2em', listStyle: 'disc' }}>
          <li style={{ paddingBottom: 'var(--space-ui-small-l)' }}>
            Colours, spacing, radii and transitions come from CSS variables — never a hex, a px or a
            Tailwind colour utility.
          </li>
          <li style={{ paddingBottom: 'var(--space-ui-small-l)' }}>
            Type is a <code>typo-*</code> class, never
            <code> text-xl</code> or an inline font size.
          </li>
          <li style={{ paddingBottom: 'var(--space-ui-small-l)' }}>
            Tailwind is for layout only — flex, grid, width, position, responsive breakpoints.
          </li>
          <li style={{ paddingBottom: 'var(--space-ui-small-l)' }}>
            Reuse the DS component. An inline <code>&lt;span&gt;{'{price}'},-&lt;/span&gt;</code>
            will not pick up changes to <code>Price</code>.
          </li>
          <li>
            Responsive variants are media queries, not props. A component adapts itself — there is no
            <code> resolution</code> prop anywhere in this system.
          </li>
        </ul>
      </section>
    </article>
  );
}

OverviewPage.propTypes = {
  brand: PropTypes.oneOf(['CLS', 'ABC', 'Overall']),
  /** Nav groups, passed down by the shell to avoid a registry import cycle */
  groups: PropTypes.array,
  /** Flat page list, same reason */
  pages: PropTypes.array,
  /** Route prefix */
  basePath: PropTypes.string,
};
